import { useState, useEffect, useCallback } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { acquireApiToken } from '@/auth/acquireApiToken';

export interface BusinessAnalyticsRow {
  business_id: string;
  business_name: string;
  verified: string | null;
  business_type: string | null;
  created_at: string | null;

  // check-ins
  check_ins_total: number;
  check_ins_distinct_users: number;
  going_intents_submitted: number;
  last_check_in_at: string | null;

  // live check-ins
  live_check_ins_started: number;
  live_check_ins_completed: number;
  live_check_ins_active_now: number;
  live_check_in_distinct_users: number;
  live_check_in_avg_minutes: number | null;
  live_check_in_max_minutes: number | null;
  live_minutes_total: number;
  peak_concurrent: number;
  last_live_check_in_at: string | null;

  // favorites
  favorites_total: number;

  // events
  events_created: number;
  events_cancelled: number;
  events_completed: number;
  events_recurring: number;
  event_interests_going: number;
  event_interests_interested: number;
  event_interests_removed: number;
  event_analytics_rows: number;
  avg_event_conversion_pct: number | null;
  event_planned_attendees_total: number;
  event_actual_attendees_total: number;

  // deals
  deals_created: number;
  deals_active_now: number;
  deals_scheduled: number;
  deals_expired: number;
  deals_deleted: number;
  deal_views_total: number;
  deal_views_distinct_users: number;
  deal_views_from_business_page: number;
  deal_views_from_notification: number;
  deal_notifications_sent: number;
  deal_notifications_tapped: number;
  deal_notifications_failed: number;

  // business notifications
  business_notifications_created: number;
  business_notifications_sent_rows: number;
  business_notification_deliveries_sent: number;
  business_notification_deliveries_delivered: number;
  business_notification_deliveries_opened: number;
  business_notification_max_subscriber_count: number | null;

  // weekly rollup of push notifications
  weekly_notifications_sent: number;
  weekly_notifications_opened: number;
  weekly_attributed_visits: number;

  // cover
  cover_history_entries: number;
  cover_history_set: number;
  cover_history_updated: number;
  cover_history_cleared: number;
  cover_history_expired: number;
  cover_currently_active: boolean;
  cover_current_amount: number | null;
  cover_notifications_sent: number;
  cover_notifications_tapped: number;
  cover_notifications_failed: number;

  // rollup presence
  weekly_summary_weeks: number;
  latest_summary_week: string | null;
  latest_week_total_visitors: number | null;
  latest_week_trend_status: string | null;
  latest_week_growth_rate_percent: number | null;
  latest_week_avg_session_minutes: number | null;
  latest_dominant_crowd_level: string | null;
  latest_crowd_week: string | null;

  // audit
  admin_audit_events: number;
}

export interface BusinessAnalyticsResponse {
  since: string;
  until: string;
  business_count: number;
  businesses: BusinessAnalyticsRow[];
}

const EMPTY: BusinessAnalyticsResponse = {
  since: '',
  until: '',
  business_count: 0,
  businesses: [],
};

export interface BusinessAnalyticsParams {
  since?: string;
  until?: string;
}

export function useBusinessAnalytics(
  apiUrl: string,
  params: BusinessAnalyticsParams = {},
): {
  data: BusinessAnalyticsResponse;
  loading: boolean;
  error: string | null;
  refetch: () => void;
} {
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();

  const { since, until } = params;

  const [data, setData] = useState<BusinessAnalyticsResponse>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [trigger, setTrigger] = useState(0);

  const refetch = useCallback(() => setTrigger((t) => t + 1), []);

  useEffect(() => {
    if (!isAuthenticated || accounts.length === 0) return;

    let isMounted = true;

    const fetchData = async () => {
      if (!apiUrl) {
        if (isMounted) {
          setError('API URL is not configured for this environment');
          setLoading(false);
        }
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const tokenResponse = { accessToken: await acquireApiToken(instance, accounts[0]) };
        const headers = {
          Authorization: `Bearer ${tokenResponse.accessToken}`,
          'Content-Type': 'application/json',
        };

        const search = new URLSearchParams();
        if (since) search.set('since', since);
        if (until) search.set('until', until);
        const qs = search.toString();
        const url = `${apiUrl}/admin/b2b/business-analytics-overview${qs ? `?${qs}` : ''}`;

        const res = await fetch(url, { headers });
        const json = await res.json();
        if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);

        if (isMounted) {
          setData(json);
          setError(null);
        }
      } catch (err: any) {
        if (isMounted) {
          setError(err?.message ?? 'Failed to load business analytics');
          setData(EMPTY);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchData();
    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, instance, accounts, apiUrl, since, until, trigger]);

  return { data, loading, error, refetch };
}
